/**
 * Data loading + selection mutations for the SFOC viewer.
 *
 * Ships and window are the Model Viewer's shared selection (`modelShips`,
 * `modelEnd`), picked in the sidebar's Query card. SFOC follows them: a newly
 * picked ship opens its matrix, a dropped one takes its curves with it, and the
 * window's end sets the curve date. Curves are fetched only while SFOC is on
 * screen (`ensurePickedLoaded`), so picking ships for Service Power does not
 * start 36 upstream probes per ship. Selection lives in `sfocSelected` as opaque
 * curve keys; the helpers here mutate that set and the `sfocExpanded` set.
 */

import { getSfocCurves } from '../../api/endpoints.ts';
import {
  sfocDate,
  sfocData,
  sfocSelected,
  sfocExpanded,
  sfocLoadingShips,
  sfocError,
  modelShips,
  modelEnd,
} from '../../state/store.ts';
import { parseKey } from './sfoc-selection.ts';
import { userMessage } from '../../api/errors.ts';

/** Mutate a Set atom immutably (new Set so StoreController sees the change). */
function mutateSet<T>(atom: { value: Set<T>; set: (s: Set<T>) => void }, fn: (s: Set<T>) => void): void {
  const next = new Set(atom.value);
  fn(next);
  atom.set(next);
}

/**
 * The date curves are looked up at for a window: its last instant's UTC day,
 * never later than today.
 *
 * The end of a window is exclusive - every preset ends at midnight tomorrow -
 * so the last instant is one second before it. A curve released after the
 * window closed is not the one that applied during it, and a future date would
 * only ask the service for a model nobody has had yet.
 */
export function curveDateFor(end: string, now: Date = new Date()): string {
  const ms = Date.parse(/(?:Z|[+-]\d\d:\d\d)$/i.test(end) ? end : `${end}Z`);
  const today = now.toISOString().slice(0, 10);
  if (!Number.isFinite(ms)) return today;
  const last = new Date(ms - 1000).toISOString().slice(0, 10);
  return last < today ? last : today;
}

/** Fetch a ship's curves if not already cached / in flight. */
export async function ensureShipLoaded(ship: string): Promise<void> {
  if (sfocData.value[ship] || sfocLoadingShips.value.has(ship)) return;
  const date = sfocDate.value;
  mutateSet(sfocLoadingShips, (s) => s.add(ship));
  try {
    const curves = await getSfocCurves(ship, date);
    if (date === sfocDate.value) sfocData.set({ ...sfocData.value, [ship]: curves });
  } catch (e) {
    if (date === sfocDate.value) sfocError.set(userMessage(e));
  } finally {
    mutateSet(sfocLoadingShips, (s) => s.delete(ship));
  }
  // The date moved while this was in flight, and the reload that move asked for
  // was turned away because this fetch still held the ship. The answer is for a
  // date no longer on screen: drop it and fetch the one that is.
  if (date !== sfocDate.value && modelShips.value.includes(ship)) void ensureShipLoaded(ship);
}

/** Fetch every picked ship's curves that are not cached yet - called while the
 *  SFOC viewer is showing. */
export function ensurePickedLoaded(): void {
  for (const ship of modelShips.value) void ensureShipLoaded(ship);
}

/** Change the lookup date: drop the cache; the viewer refetches what it shows.
 *  Selections persist by key. */
export function setSfocDate(date: string): void {
  if (!date || date === sfocDate.value) return;
  sfocDate.set(date);
  sfocData.set({});
  sfocError.set(null);
}

/**
 * Follow the picked ships. A newly picked ship opens its matrix; a dropped one
 * takes its curve selection and its open matrix with it, so nothing is drawn for
 * a ship the selector no longer shows.
 */
function followShips(next: string[], prev: string[]): void {
  const keep = new Set(next);
  const added = next.filter((s) => !prev.includes(s));
  if (prev.some((s) => !keep.has(s))) {
    mutateSet(sfocSelected, (sel) => {
      for (const key of [...sel]) if (!keep.has(parseKey(key).ship)) sel.delete(key);
    });
  }
  mutateSet(sfocExpanded, (open) => {
    for (const ship of [...open]) if (!keep.has(ship)) open.delete(ship);
    for (const ship of added) open.add(ship);
  });
}

let lastShips = modelShips.value;
modelShips.subscribe((next) => {
  const prev = lastShips;
  lastShips = next;
  followShips(next, prev);
});
modelEnd.subscribe((end) => setSfocDate(curveDateFor(end)));
sfocDate.set(curveDateFor(modelEnd.value));

/** Expand/collapse a ship's matrix, loading its curves on first expand. */
export function toggleExpanded(ship: string): void {
  const isOpen = sfocExpanded.value.has(ship);
  mutateSet(sfocExpanded, (s) => (isOpen ? s.delete(ship) : s.add(ship)));
  if (!isOpen) void ensureShipLoaded(ship);
}

/** Deselect every curve. The picked ships stay - the selector owns those. */
export function clearAll(): void {
  sfocSelected.set(new Set());
}

/** Toggle a single curve key. */
export function toggleCurve(key: string): void {
  mutateSet(sfocSelected, (s) => (s.has(key) ? s.delete(key) : s.add(key)));
}

/** Set a group of keys on or off in one shot. */
export function setKeys(keys: string[], on: boolean): void {
  if (keys.length === 0) return;
  mutateSet(sfocSelected, (s) => {
    for (const k of keys) {
      if (on) s.add(k);
      else s.delete(k);
    }
  });
}

/** Toggle a group: if any are selected, clear them all; otherwise select all. */
export function toggleKeys(keys: string[]): void {
  const anyOn = keys.some((k) => sfocSelected.value.has(k));
  setKeys(keys, !anyOn);
}
